import { BookOpen, Ruler, ArrowUpDown, Wrench, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { VehiclePicker } from "../components/VehiclePicker";
import { useVehicle } from "../context/VehicleContext";
import { useUnits } from "../context/UnitsContext";
import { UnitToggle } from "../components/UnitToggle";

const SpecRow = ({ label, spec, testId, isToe }) => {
  const { toeNum, toeSuffix } = useUnits();
  const target = isToe ? `${toeNum(spec.target, true)}${toeSuffix}` : `${spec.target >= 0 ? "+" : ""}${spec.target.toFixed(2)}°`;
  const range = isToe
    ? `${toeNum(spec.min)} … ${toeNum(spec.max)}${toeSuffix}`
    : `${spec.min.toFixed(2)}° … ${spec.max.toFixed(2)}°`;
  return (
  <tr data-testid={testId} className="border-b border-steel/50 last:border-0">
    <td className="py-3 pr-4 font-mono text-xs text-slate-300 uppercase tracking-wide">{label}</td>
    <td className="py-3 px-2 font-mono text-sm text-amber text-center tabular-nums">{target}</td>
    <td className="py-3 px-2 font-mono text-sm text-white text-center tabular-nums">{range}</td>
    <td className="py-3 pl-2 text-right">
      <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm ${spec.adjustable ? "text-amber bg-amber/10" : "text-slate-500 bg-steel/30"}`}>
        {spec.adjustable ? "Adjustable" : "Fixed"}
      </span>
    </td>
  </tr>
  );
};

const SpecTable = ({ title, icon: Icon, rows }) => (
  <section className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-amber" />
      <h2 className="font-heading font-bold uppercase tracking-widest text-lg">{title}</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[460px]">
        <thead>
          <tr className="border-b border-steel">
            <th className="py-2 pr-4 text-left font-mono text-[10px] text-slate-500 uppercase tracking-widest">Angle</th>
            <th className="py-2 px-2 font-mono text-[10px] text-slate-500 uppercase tracking-widest">Target</th>
            <th className="py-2 px-2 font-mono text-[10px] text-slate-500 uppercase tracking-widest">Range</th>
            <th className="py-2 pl-2 text-right font-mono text-[10px] text-slate-500 uppercase tracking-widest">Type</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  </section>
);

export default function SpecVault() {
  const { specs } = useVehicle();
  const { t } = useTranslation();
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
          <BookOpen size={14} /> {t("spec.kicker")}
        </div>
        <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">{t("spec.title")}</h1>
        <p className="text-slate-400 text-sm mt-2 font-mono">{specs ? specs.vehicle : t("spec.choose")}</p>
      </div>

      <div className="space-y-6">
        <VehiclePicker />

        {!specs ? (
          <div data-testid="spec-vault-empty" className="rounded-md border border-amber/40 bg-amber/5 p-6 text-center">
            <p className="text-sm text-slate-300">{t("spec.choose")}</p>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-end" data-testid="spec-vault-units">
          <UnitToggle />
        </div>
        <SpecTable title={t("spec.frontSusp")} icon={Ruler} rows={[
          <SpecRow key="fc" testId="spec-front-camber" label={t("common.camber")} spec={specs.front.camber} />,
          <SpecRow key="fca" testId="spec-front-caster" label={t("common.caster")} spec={specs.front.caster} />,
          <SpecRow key="ft" testId="spec-front-toe" label={t("common.totalToe")} spec={specs.front.totalToe} isToe />
        ]} />

        <SpecTable title={t("spec.rearSusp")} icon={ArrowUpDown} rows={[
          <SpecRow key="rc" testId="spec-rear-camber" label={t("common.camber")} spec={specs.rear.camber} />,
          <SpecRow key="rt" testId="spec-rear-toe" label={t("common.totalToe")} spec={specs.rear.totalToe} isToe />,
        ]} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {specs.rideHeight && (
          <section data-testid="spec-ride-height" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
            <div className="flex items-center gap-2 mb-4">
              <Info size={18} className="text-amber" />
              <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Ride Height (Z/D)</h2>
            </div>
            <dl className="space-y-3 font-mono text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Front Z-Height</dt><dd className="text-white">{specs.rideHeight.zHeightFront}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Rear D-Height</dt><dd className="text-white">{specs.rideHeight.dHeightRear}</dd></div>
            </dl>
            <p className="text-xs text-slate-400 mt-4 leading-relaxed">{specs.rideHeight.notes}</p>
          </section>
          )}

          {specs.torque && (
          <section data-testid="spec-torque" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
            <div className="flex items-center gap-2 mb-4">
              <Wrench size={18} className="text-amber" />
              <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Torque Specs</h2>
            </div>
            <dl className="space-y-3 font-mono text-sm">
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Cam Bolt Nut</dt><dd className="text-white text-right">{specs.torque.camBoltNut}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-400">Tie Rod Jam Nut</dt><dd className="text-white text-right">{specs.torque.tieRodJamNut}</dd></div>
            </dl>
            <p className="text-xs text-slate-400 mt-4 leading-relaxed">
              Hold the cam bolt head steady while torquing the nut. Always set rear angles first on a 4-wheel alignment.
            </p>
          </section>
          )}
        </div>

        <div className="rounded-md border border-amber/40 bg-amber/5 p-4 flex gap-3 animate-rise">
          <Info size={18} className="text-amber shrink-0 mt-0.5" />
          <p className="text-xs text-slate-300 leading-relaxed">
            {t("spec.disclaimer", { vehicle: specs.vehicle })}
          </p>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
